
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PiwikPro from '@piwikpro/react-piwik-pro';
import logger from '@/utils/logger';

/**
 * Hook pour tracker automatiquement les pages vues avec Piwik Pro
 * Utiliser ce hook dans un composant de haut niveau comme App ou AppRoutes
 */
export const usePiwikTracking = () => {
  const location = useLocation();
  const prevPathRef = useRef<string>('');

  useEffect(() => {
    // Ne pas tracker si le chemin n'a pas changé
    if (prevPathRef.current === location.pathname) {
      return;
    }

    // Enregistrer la page vue dans Piwik Pro
    try {
      const pageTitle = document.title || `Page - ${location.pathname}`;
      
      // Using correct method from the Piwik Pro library
      // The trackPageView method is accessed via window object
      if (window._paq) {
        window._paq.push(['setDocumentTitle', pageTitle]);
        window._paq.push(['setCustomUrl', window.location.href]);
        window._paq.push(['trackPageView']);
      }
      
      logger.log(`Piwik tracking: ${pageTitle} at ${location.pathname}`);
      prevPathRef.current = location.pathname;
    } catch (error) {
      logger.error('Erreur lors du tracking Piwik:', error);
    }
  }, [location]);

  // Fonction utilitaire pour tracker des événements personnalisés
  const trackEvent = (category: string, action: string, name?: string, value?: number) => {
    try {
      // Using correct method from the Piwik Pro library
      // The trackEvent method is accessed via window object
      if (window._paq) {
        window._paq.push(['trackEvent', category, action, name, value]);
      }
      logger.log(`Piwik event tracked: ${category} - ${action}`);
    } catch (error) {
      logger.error('Erreur lors du tracking d\'événement Piwik:', error);
    }
  };

  return { trackEvent };
};
